import moment from 'moment';

import HIGHLIGHT_MODES from '@modules/JourneyBuilder/Components/Charts/SankeyV2/highlight-modes';
import TAG_ROUTE_TYPES from '@modules/JourneyBuilder/Components/Charts/SankeyV2/tag-route-types';
import {debounceWithLeadingCallAsync} from "@modules/JourneyBuilder/Components/Charts/SankeyV2/helpers";

import api from "@modules/JourneyBuilder/api";
import sankeyApi from "@modules/JourneyBuilder/Components/Charts/sankey-api";

export default {
    namespaced: true,
    state: {
        /**
         * @type {String|undefined} 
         */
        journeyId: undefined,

        /**
         * @type {SankeyNode[]}
         */
        sankeyData: [],

        /**
         * @type {SankeyType[]}
         */
        sankeyTypes: [],

        /**
         * @type {String|undefined} Selected sankey brick id
         */
        selectedBrickId: undefined,

        /**
         * @type {Number|undefined}
         */
        inflowPeriodId: undefined,
        
        /**
         * @type {Array.<String>}
         */
        productNames: [],

        /**
         * @type {Boolean}
         */
        isLoadingPeriodRange: false,
        
        /**
         * @type {String|undefined}
         */
        periodStart: undefined,

        /**
         * @type {String|undefined}
         */
        periodEnd: undefined,

        /**
         * @type {Boolean}
         */
        displayGoalRoutes: true,

        /**
         * @type {HIGHLIGHT_MODES|undefined}
         */
        highlightMode: undefined,

        /**
         * @type {TAG_ROUTE_TYPES|undefined}
         */
        tagRouteType: undefined
    },
    getters: {
        //Hack; we only want to trigger watches once per update cycle to prevent DDOSing ourselves/race conditions :-)
        watchableReportStatisticsParameters(state){
            const sortedProductNames = [...state.productNames]
                .sort((left, right) => left.localeCompare(right));

            return `${state.journeyId}|${state.periodStart}|${state.periodEnd}|${state.inflowPeriodId}|${sortedProductNames.join('|')}`;
        },
        /**
         * Parameters to be used for reading sankey/report statistics widget data
         * @param state
         * @return {JourneyReportStatisticsParameters}
         */
        reportStatisticsParameters(state){
            return {
                periodStart: state.periodStart,
                periodEnd: state.periodEnd,
                inflowPeriodId: state.inflowPeriodId,
                productNames: state.productNames
            }
        },
        /**
         * Determines whether we have enough data to be able to actually load our report
         * @param state
         * @return {Boolean}
         */
        canLoadSankeyData(state) {
            return state.periodStart !== undefined && state.inflowPeriodId !== undefined;
        },

        /**
         * Getter for all sankey data eligible for rendering
         * @param state
         * @return {SankeyNode[]}
         */
        activeSankeyData(state) {
            //Completely disregard goal/outflow nodes when their assigned tag
            //is not explicitly displayed
            //upon a change in tag type, we should just re-render our entire chart
            return state.sankeyData.filter(node => {
                if(node.isGoal) {
                    return state.tagRouteType === TAG_ROUTE_TYPES.GOAL;
                }

                if(node.isOutflow) {
                    return state.tagRouteType === TAG_ROUTE_TYPES.OUTFLOW;
                }

                return true;
            });
        },
        
        /**
         * Array containing all supported highlight modes based on the current sankey data
         * @param state
         * @param getters
         * @return {String[]} supported highlight modes
         */
        supportedHighlightModes(state, getters){
            const supportedHighlightModes = [];
            
            //Goal nodes with a total of 0 will skew results, thus we don't count those
            const hasGoalNodes = getters.activeSankeyData.some(n => n.isGoal && n.total !== 0);
            
            //Not the most efficient solution, will suffice for now
            //Select all final (= no other node with from = node.id) non-root nodes (= node.from = truthy)
            const hasRegularEndNodes = getters.activeSankeyData.some(n => n.from && !getters.activeSankeyData.some(nP => nP.from === n.id));
            
            if(hasGoalNodes){
                supportedHighlightModes.push(HIGHLIGHT_MODES.LEAST_SUCCESSFUL_ROUTE, HIGHLIGHT_MODES.MOST_SUCCESSFUL_ROUTE);
            }
            
            if(hasRegularEndNodes){
                supportedHighlightModes.push(HIGHLIGHT_MODES.LEAST_TAKEN_ROUTE, HIGHLIGHT_MODES.MOST_TAKEN_ROUTE);
            }
            
            return supportedHighlightModes;
        },

        /**
         * Array containing all supported tag route types based on the current sankey data
         * @param state
         * @return {String[]} supported tag route types
         */
        supportedTagRouteTypes(state) {
            const supportedTypes = [];
            
            if(state.sankeyData.some(n => n.isGoal)) {
                supportedTypes.push(TAG_ROUTE_TYPES.GOAL);
            }

            if(state.sankeyData.some(n => n.isOutflow)) {
                supportedTypes.push(TAG_ROUTE_TYPES.OUTFLOW);
            }
            
            return supportedTypes;
        },
        
        /**
         * The currently selected nodes
         * @param state
         * @return {SankeyNode|null}
         */
        selectedNodes(state){
            if(!state.sankeyData || !state.selectedBrickId){
                return [];
            }
            
            return state.sankeyData.filter(node => node.brickId === state.selectedBrickId);
        }
    },
    mutations: {
        setJourneyId(state, journeyId){
            state.journeyId = journeyId;
        },
        setSankeyData(state, sankeyData){
            state.sankeyData = sankeyData;
        },
        setSankeyTypes(state, sankeyTypes){
            state.sankeyTypes = sankeyTypes;
        },
        setSelectedBrickId(state, brickId){
            state.selectedBrickId = brickId;
        },
        setPeriodStart(state, start){
            state.periodStart = start;
        },
        setIsLoadingPeriodRange(state, value){
            state.isLoadingPeriodRange = value;
        },
        setPeriodEnd(state, end){
            state.periodEnd = end;
        },
        setInflowPeriodId(state, inflowPeriodId) {
            state.inflowPeriodId = inflowPeriodId;
        },
        setProductNames(state, productNames){
            state.productNames = productNames;
        },
        setDisplayGoalRoutes(state, displayGoalRoutes){
            state.displayGoalRoutes = displayGoalRoutes;
        },
        setHighlightMode(state, highlightMode){
            state.highlightMode = highlightMode;
        },
        setTagRouteType(state, tagRouteType){
            state.tagRouteType = tagRouteType;
        }
    },
    actions: {
        loadSankeyData: debounceWithLeadingCallAsync(async ({state, getters, commit}) => {
            if(!getters.canLoadSankeyData) {
                return;
            }
            
            const remoteData = await sankeyApi.getJourneyDataFromEndpoint(
                state.journeyId, 
                getters.reportStatisticsParameters
            );
            
            remoteData.types.sort(({name: left}, {name: right}) => {
                left = left.toLowerCase();
                right = right.toLowerCase();

                //Audience should always be first
                if(left === 'audience' || right === 'audience'){
                    return left === 'audience' ? -1 : 1;
                }

                //Sort the rest alphabetically
                return left.localeCompare(right);
            });
            
            commit('setSankeyData', remoteData.data);
            commit('setSankeyTypes', remoteData.types);
        }),
        loadDefaultPeriodRange: debounceWithLeadingCallAsync(async ({commit}) => {
            commit('setIsLoadingPeriodRange', true);

            try {
                const {start, end} = await api.getStatisticsPeriodDateRange();

                commit('setPeriodStart', moment(start).format('YYYY-MM-DD'));
                commit('setPeriodEnd', moment(end).format('YYYY-MM-DD'));
            } catch(e){
                const now = moment();
                
                //Fallback to the past 14 days, our Sankey relies on having a date range
                //we'd rather have an incorrect default date range than no Sankey
                commit('setPeriodStart', now.format('YYYY-MM-DD'));
                commit('setPeriodEnd', now.add(-14, 'days').format('YYYY-MM-DD'));
            } finally {
                commit('setIsLoadingPeriodRange', false);
            }
        })
    }
};